import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useScrollLock } from '../../../_utils/useScrollLock';
import './BottomSheet.css';

// Shared bottom sheet for the client side. Replaces the bespoke centre
// dialogs (booking cancel, wallet detail/add/design, account editors) with
// the one modal shape phones actually use: slides up from the bottom,
// drags down to dismiss, scrolls its own content without chaining to the
// page behind, and respects the home-indicator safe area.
//
// Usage:
//   <BottomSheet open={isOpen} onClose={close} title="Cancel booking">
//     ...content...
//   </BottomSheet>
//
// The sheet stays mounted briefly after `open` flips false so the exit
// animation can play. Dragging works from the header on touch; the
// content area keeps native scrolling.
const CLOSE_MS = 240;
const DISMISS_DISTANCE = 90;

const BottomSheet = ({ open, onClose, title, children, footer, maxWidth = 480, className = '' }) => {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragRef = useRef({ active: false, startY: 0, delta: 0 });
  const closeTimer = useRef(null);

  // Mount/unmount around the animations.
  useEffect(() => {
    if (open) {
      clearTimeout(closeTimer.current);
      setClosing(false);
      setMounted(true);
      return undefined;
    }
    if (mounted) {
      setClosing(true);
      closeTimer.current = setTimeout(() => {
        setMounted(false);
        setClosing(false);
        setDragY(0);
      }, CLOSE_MS);
    }
    return () => clearTimeout(closeTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lock the page's scroll while the sheet is up (html AND body - which one
  // scrolls varies; locking body alone leaves the document scrollable).
  // Shared ref-counted scroll lock (covers body + root): overlapping
  // overlays must not restore each other's locked styles.
  useScrollLock(mounted);

  // Escape closes.
  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, onClose]);

  const beginDrag = useCallback((clientY) => {
    dragRef.current = { active: true, startY: clientY, delta: 0 };
  }, []);

  const moveDrag = useCallback((clientY) => {
    if (!dragRef.current.active) return;
    const delta = Math.max(0, clientY - dragRef.current.startY); // downward only
    dragRef.current.delta = delta;
    setDragY(delta);
  }, []);

  const endDrag = useCallback(() => {
    if (!dragRef.current.active) return;
    const delta = dragRef.current.delta;
    dragRef.current.active = false;
    if (delta > DISMISS_DISTANCE) {
      if (onClose) onClose();
      // dragY carries into the closing animation start point
    } else {
      setDragY(0);
    }
  }, [onClose]);

  if (!mounted) return null;

  return (
    <div
      className={`bottom-sheet-root${closing ? ' closing' : ''}${className ? ` ${className}` : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Dialog'}
    >
      <div className="bottom-sheet-backdrop" onClick={() => onClose && onClose()} />
      <div
        className="bottom-sheet"
        style={{
          maxWidth,
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: dragRef.current.active ? 'none' : undefined,
        }}
      >
        <div
          className="bottom-sheet-grab"
          onTouchStart={(e) => beginDrag(e.touches[0].clientY)}
          onTouchMove={(e) => moveDrag(e.touches[0].clientY)}
          onTouchEnd={endDrag}
          onTouchCancel={endDrag}
        >
          {title && <h2 className="bottom-sheet-title">{title}</h2>}
          <button type="button" className="bottom-sheet-close" onClick={() => onClose && onClose()} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="bottom-sheet-content">{children}</div>
        {footer && <div className="bottom-sheet-footer">{footer}</div>}
      </div>
    </div>
  );
};

export default BottomSheet;
